import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import FAQSchema from '@/components/FAQSchema';
import SeoBreadcrumbs from '@/components/SeoBreadcrumbs';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const FAQs = () => {
  const faqs = [
    {
      question: "What is a property marketing floor plan?",
      answer: "A property marketing floor plan is a professional 2D drawing showing the layout of a property, including rooms, dimensions, and flow. It's used by estate agents on Rightmove, Zoopla, and in property brochures to help potential buyers or tenants visualise the space before viewing."
    },
    {
      question: "Do estate agents need floor plans for listings?",
      answer: "Yes, floor plans significantly increase buyer engagement. Research shows that property listings with floor plans receive up to 30% more enquiries than those without. Rightmove and Zoopla both recommend floor plans for all property listings, and many buyers expect to see them."
    },
    {
      question: "How much does a property floor plan cost in the UK?",
      answer: "At House Plan Direct, floor plans start from just £40 for properties with up to 3 rooms. Additional rooms cost £5 each. Optional extras include colour floor plans (+£10), furniture layout (+£25), and symbols & key (+£25). All orders include JPEG, PDF, and DWG formats."
    },
    {
      question: "Can a sketch be converted into a professional floor plan?",
      answer: "Yes! Simply upload your hand-drawn sketch, a photo of your floor plan, or an old plan. We'll convert it into a professional, listing-ready floor plan suitable for Rightmove, Zoopla, and property brochures within 24-48 hours."
    },
    {
      question: "How long does it take to create a floor plan?",
      answer: "Standard turnaround is 24-48 hours. We offer fast delivery for urgent property listings. Simply upload your sketch and we'll have your professional floor plan ready quickly."
    },
    {
      question: "What file formats do you provide?",
      answer: "All floor plan orders include three formats: JPEG (perfect for Rightmove, Zoopla, and digital marketing), PDF (high quality for property brochures and printing), and DWG (industry-standard CAD format for architects and builders to edit)."
    },
    {
      question: "Are your floor plans suitable for Rightmove and Zoopla?",
      answer: "Yes, our floor plans are specifically formatted for UK property portals including Rightmove, Zoopla, OnTheMarket, and PrimeLocation. The JPEG format is optimised for online listings."
    },
    {
      question: "Do you provide floor plans for rental properties?",
      answer: "Yes, we provide floor plans for both sales and rentals. Landlords and letting agents use our floor plans for Rightmove, Zoopla, OpenRent, SpareRoom, and other rental listing sites. Floor plans help attract quality tenants by clearly showing the property layout."
    },
    {
      question: "Can I use the floor plan for EPC assessments?",
      answer: "Yes, our floor plans show clear room layouts and dimensions suitable for EPC (Energy Performance Certificate) assessments. Many EPC assessors and landlords use our floor plans as part of their property documentation."
    },
    {
      question: "What do I need to provide to get a floor plan?",
      answer: "Simply provide a sketch or photo showing your property's room layout. Include measurements if you have them - this helps ensure accuracy. We can work from hand-drawn sketches, photos of existing plans, old floor plans, or scanned PDFs."
    },
    {
      question: "Do you cover the whole of the UK?",
      answer: "Yes, we're a UK-wide online service. Simply upload your sketch and we'll convert it into a professional floor plan - no site visit required. We serve estate agents, landlords, property sellers, and property photographers across England, Wales, Scotland, and Northern Ireland."
    },
    {
      question: "What happens if there's an error on my floor plan?",
      answer: "We stand behind our work. Any errors in the conversion that are our responsibility will be corrected free of charge. If you require amendments or changes to your specifications after delivery, these are charged at £25 per hour with a minimum charge of £10."
    },
    {
      question: "Where can I get a floor plan for selling a house in the UK?",
      answer: "House Plan Direct provides professional floor plans for house sales across the UK. Simply upload your sketch or photo, and we'll deliver a listing-ready floor plan within 24-48 hours. Our plans are perfect for estate agent listings, property brochures, and online portals like Rightmove and Zoopla."
    },
    {
      question: "How do estate agents create property floor plans?",
      answer: "Estate agents typically either measure properties themselves or hire professional floor plan services. With House Plan Direct, agents can simply sketch the layout and upload it - we convert sketches into professional floor plans within 24-48 hours, starting from £40."
    },
    {
      question: "Can I create my own floor plan for free?",
      answer: "Yes. The House Plan Direct Floor Plan Creator lets you draw a floor plan online for free - enter exact wall lengths, add rooms, doors and windows, and present a marketing-ready layout. Creating and editing is free; unlocking the finished project costs a one-off £2.99."
    },
    {
      question: "Can I measure an existing floor plan myself?",
      answer: "Yes. Plan Measure lets you upload a PDF or image of an existing plan, calibrate it against one known dimension, and measure lengths, areas and rooms for free directly in your browser - no software to install."
    }
  ];

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "FAQs", path: "/faqs" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        pageId="faqs"
        defaultTitle="Floor Plan & HPD Draw FAQs | House Plan Direct"
        defaultDescription="Common questions about property floor plans answered: How much do floor plans cost? Are they suitable for Rightmove? Can you convert a sketch? UK-wide floor plan service."
        aiSummary="Find answers to common questions about property floor plans for estate agents, landlords, and property sellers. Learn about pricing (from £40), turnaround times (24-48 hours), formats (JPEG, PDF, DWG), and how to convert sketches into professional floor plans for Rightmove and Zoopla listings."
        keywords="property floor plan FAQ, estate agent floor plan questions, Rightmove floor plan FAQ, floor plan cost UK, floor plan from sketch FAQ"
      />
      <FAQSchema faqs={faqs} />
      <SeoBreadcrumbs items={breadcrumbs} />
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-12" data-testid="faqs-header">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
          <p className="text-lg text-gray-600">
            Find answers to common questions about our property floor plan services.
          </p>
        </div>

        {/* FAQ Accordion */}
        <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
          {faqs.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="bg-gray-50 rounded-lg px-6 border border-gray-100"
              data-testid={`faq-item-${index}`}
            >
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-4">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 pb-4">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* CTA Section */}
        <div className="mt-12 bg-blue-600 text-white rounded-xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to Get Your Floor Plan?</h2>
          <p className="text-blue-100 mb-6">
            Upload your sketch and receive a professional, listing-ready floor plan within 24-48 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/floor-plan-creator">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700" data-testid="faqs-link-floor-plan-creator">
                Create Your Own — Free <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/order">
              <Button size="lg" variant="secondary" className="bg-white text-blue-600 hover:bg-gray-100">
                Upload Your Sketch <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-700">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQs;
